import React, { useState, useEffect } from "react";
import StoreEmployee from "@apis/storeEmployee";
import Fields from "@components/fields";
import { Form, Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";
import DropDown from "@components/fields/dropdown";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import Role from "@apis/role";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  ShieldCheck,
  User,
  KeyRound,
} from "lucide-react";

const FormikDataFiller = ({ data }: { data: any }) => {
  const { setValues, values } = useFormikContext<any>();

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setValues({
        ...values,
        name: data.name || "",
        email: data.email || "",
        phoneNo: data.phoneNo || "+977",
        avatar: data.avatar ? { url: data.avatar } : null,
        roleId: data.roleId || "",
        isActive: data.isActive ?? true,
      });
    }
  }, [data]);

  return null;
};

const CreateEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [initialUserData, setInitialUserData] = useState<any>(null);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    Role.list();
    if (isEdit) {
      fetchEmployeeDetails();
    }
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      const response = await StoreEmployee.find(Number(id));
      const userData = response?.data || response;
      if (userData && (userData.id || userData.email)) {
        setInitialUserData(userData);
      } else {
        toast.error("Employee details not found");
      }
    } catch (error) {
      console.error("Failed to fetch employee details:", error);
      toast.error("Failed to load employee details");
    } finally {
      setLoading(false);
    }
  };

  const rolesState = useSelector((state: RootState) => state.roles);
  const roles = Array.isArray(rolesState.data)
    ? rolesState.data
    : rolesState.items || [];

  const roleOptions = roles.map((role: any) => ({
    title: role.name,
    value: role.id,
    permissions: role.permissions || [],
  }));

  const initialValues = {
    name: initialUserData?.name || "",
    email: initialUserData?.email || "",
    phoneNo: initialUserData?.phoneNo || "+977",
    password: "",
    confirmPassword: "",
    avatar: initialUserData?.avatar ? { url: initialUserData.avatar } : null,
    roleId: initialUserData?.roleId || "",
    isActive: initialUserData?.isActive ?? true,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      if (
        !values.name ||
        !values.email ||
        !values.phoneNo ||
        (!isEdit && !values.password)
      ) {
        toast.error("Please fill in all required fields.");
        setSubmitting(false);
        return;
      }

      if (!isEdit && values.password !== values.confirmPassword) {
        toast.error("Passwords do not match.");
        setSubmitting(false);
        return;
      }

      let formattedPhone = values.phoneNo.trim();
      if (!formattedPhone.startsWith("+")) {
        if (formattedPhone.startsWith("977")) {
          formattedPhone = "+" + formattedPhone;
        } else {
          formattedPhone = "+977" + formattedPhone;
        }
      }

      const payload: any = {
        name: values.name,
        email: values.email,
        phoneNo: formattedPhone,
      };

      if (values.password) {
        payload.password = values.password;
      }

      if (values.roleId) {
        payload.roleId = values.roleId;
      }

      if (values.isActive !== undefined) {
        payload.isActive = values.isActive;
      }

      let promise;
      if (isEdit) {
        promise = StoreEmployee.update(Number(id), payload);
      } else {
        promise = StoreEmployee.create(payload);
      }

      await toast.promise(promise, {
        pending: isEdit ? "Updating team member..." : "Creating team member...",
        success: isEdit
          ? "Team member updated successfully!"
          : "Team member created successfully!",
        error: isEdit
          ? "Failed to update team member"
          : "Failed to create team member",
      });

      navigate("/store/users/sub-admins");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div style={{ margin: "0 auto", paddingBottom: "60px" }}>
      {/* Header Section */}
      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            <ChevronRight style={{ transform: "rotate(180deg)" }} size={20} />
          </button>
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: "#0f172a",
                margin: "0 0 6px 0",
              }}
            >
              {isEdit ? "Update Team Member" : "Add Team Member"}
            </h1>
            <p style={{ margin: 0, color: "#64748b", fontSize: "15px" }}>
              {isEdit
                ? "Modify team member information and role assignment."
                : "Add a new team member to your store."}
            </p>
          </div>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          const selectedRole = roleOptions.find(
            (r) => r.value === values.roleId,
          );
          const rolePermissions = selectedRole?.permissions || [];

          return (
            <Form>
              <FormikDataFiller data={initialUserData} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 350px",
                  gap: "32px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "32px",
                  }}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "40px",
                      border: "1px solid #f1f5f9",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "32px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          color: "#f23236",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <User size={20} />
                      </div>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 800,
                          color: "#0f172a",
                          margin: 0,
                        }}
                      >
                        Profile Details
                      </h3>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                      }}
                    >
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Fields.Input
                          name="name"
                          label="Full Name *"
                          placeholder="E.g. John Doe"
                          required
                        />
                      </div>
                      <Fields.Input
                        name="email"
                        label="Email Address *"
                        type="email"
                        placeholder="employee@store.com"
                        required
                      />
                      <Fields.Input
                        name="phoneNo"
                        label="Phone Number *"
                        placeholder="98XXXXXXXX"
                        required
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "40px",
                      border: "1px solid #f1f5f9",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "32px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          color: "#f23236",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ShieldCheck size={20} />
                      </div>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 800,
                          color: "#0f172a",
                          margin: 0,
                        }}
                      >
                        Security & Role
                      </h3>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                        marginBottom: "32px",
                      }}
                    >
                      <Fields.Input
                        name="password"
                        label={isEdit ? "New Password" : "Password *"}
                        type="password"
                        placeholder={
                          isEdit
                            ? "Leave blank to keep current"
                            : "Enter password"
                        }
                        required={!isEdit}
                      />
                      <Fields.Input
                        name="confirmPassword"
                        label={
                          isEdit ? "Confirm New Password" : "Confirm Password *"
                        }
                        type="password"
                        placeholder={
                          isEdit
                            ? "Leave blank to keep current"
                            : "Confirm password"
                        }
                        required={!isEdit}
                      />
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #f1f5f9",
                        paddingTop: "32px",
                      }}
                    >
                      <DropDown
                        name="roleId"
                        label="Assigned Role *"
                        options={roleOptions}
                        titleAsKey="title"
                        valueAsKey="value"
                        isMultiple={false}
                        placeholder="Select a functional role"
                        styleInput="width:100%"
                        onChange={(selected: any) => {
                          setFieldValue("roleId", selected);
                        }}
                      />
                      {rolePermissions.length > 0 && (
                        <div
                          style={{
                            marginTop: "24px",
                            padding: "16px",
                            background: "#f8fafc",
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <p
                            style={{
                              margin: "0 0 12px 0",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                            }}
                          >
                            Role Permissions ({rolePermissions.length})
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {rolePermissions.map((p: string, i: number) => (
                              <span
                                key={i}
                                style={{
                                  padding: "4px 8px",
                                  background: "#ffffff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  color: "#475569",
                                  fontWeight: 600,
                                }}
                              >
                                {p.split(".").pop()?.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "32px",
                  }}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "32px",
                      border: "1px solid #f1f5f9",
                      textAlign: "center",
                    }}
                  >
                    <Fields.Upload
                      name="avatar"
                      variant="profile"
                      useRawFile={true}
                      size={120}
                      label=""
                    />
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1e293b",
                        margin: "16px 0 8px 0",
                      }}
                    >
                      Team Member Avatar
                    </h4>
                  </div>
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "24px",
                      padding: "32px",
                      border: "1.5px dashed #e2e8f0",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#475569",
                        margin: "0 0 16px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <KeyRound size={16} /> Account Summary
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#64748b" }}>
                          Role
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#f23236",
                          }}
                        >
                          {selectedRole?.title || "PENDING"}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#64748b" }}>
                          Permissions
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color:
                              rolePermissions.length > 0
                                ? "#16a34a"
                                : "#64748b",
                          }}
                        >
                          {rolePermissions.length} Granted
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        padding: "16px",
                        borderRadius: "16px",
                        background: "#f23236",
                        color: "#ffffff",
                        border: "none",
                        fontWeight: 700,
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      {isSubmitting
                        ? "Saving..."
                        : isEdit
                          ? "Update Team Member"
                          : "Save Team Member"}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      style={{
                        padding: "16px",
                        borderRadius: "16px",
                        background: "#ffffff",
                        color: "#475569",
                        border: "1.5px solid #e2e8f0",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateEmployee;
